import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Card, CardBody, CardTitle, Form, FormGroup, Label, Input, Button, Row, Col, Alert, Spinner
} from 'reactstrap';
import axios from 'axios';
import { getCenterDoctorsListAPI, getNextCenterTokenAPI, submitPatientTokenAPI, getPatientListAPI } from './../../api/network/Api';
import userUtil from '../../utils/UserUtils';
import Autosuggest from 'react-autosuggest';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';

const TOKEN_TYPES = [
    { value: 'followUp', label: 'Follow Up' },
    { value: 'consultation', label: 'Consultation' }
];
const PAYMENT_MODES = [
    'Cash',
    'Cheque',
    'Debit/Credit Card',
    'Online Payment',
    'EasyPaisa / JazzCash'
];
const PAYMENT_STATUSES = [
    'paid',
    'unpaid',
    'cancelled'
];

const TokenManagement = ({ handleCloseModal }) => {
    const params = useParams();
    const cancelTokenSource = axios.CancelToken.source();
    const [centerId, setCenterId] = useState(JSON.parse(localStorage.getItem('userDetail'))?.center_id);
    const [tokenNumber, setTokenNumber] = useState('');
    const [doctors, setDoctors] = useState([]);
    const [form, setForm] = useState({
        doctor_id: '',
        token_type: 'followUp',
        comments: '',
        amount: '',
        total_discount: '0',
        net_total: '0',
        grand_total: '0',
        payment_mode: 'Cash',
        payment_status: 'paid'
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [patientSuggestions, setPatientSuggestions] = useState([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);

    useEffect(() => {
        if (!centerId) return;
        getNextCenterTokenAPI(centerId, cancelTokenSource.token)
            .then(res => setTokenNumber(res?.data?.nextToken || ''))
            .catch(() => setTokenNumber(''));
        getCenterDoctorsListAPI(centerId, cancelTokenSource.token)
            .then(res => setDoctors(res?.data?.list || []))
            .catch(() => setDoctors([]));
    }, [centerId]);

    const onSuggestionsFetchRequested = async ({ value, reason }) => {
        setPatientSearch(value);
        if (!value) {
            setPatientSuggestions([]);
            return;
        }
        // Only fetch if reason is 'input-changed', not 'input-focused'
        if (reason !== 'input-changed') return;
        setSearchLoading(true);
        if (searchTimeout) clearTimeout(searchTimeout);
        const timeout = setTimeout(async () => {
            const response = await getPatientListAPI(value, centerId, 0, 10, cancelTokenSource.token);
            if (response?.data?.rows) {
                setPatientSuggestions(response.data.rows);
            } else {
                setPatientSuggestions([]);
            }
            setSearchLoading(false);
        }, 400);
        setSearchTimeout(timeout);
    };

    const onSuggestionsClearRequested = () => {
        setPatientSuggestions([]);
        if (searchTimeout) clearTimeout(searchTimeout);
    };

    const getSuggestionValue = suggestion => suggestion.name;
    const renderSuggestion = suggestion => (
        <div>{suggestion.name} ({suggestion.id}) ({suggestion.contact_number})</div>
    );

    const onPatientSelected = (event, { suggestion }) => {
        setSelectedPatient(suggestion);
        setPatientSearch(suggestion.name);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        let updatedForm = { ...form, [name]: value };
        // Auto-calculate net_total and grand_total
        if (name === 'amount' || name === 'total_discount') {
            const amount = parseFloat(name === 'amount' ? value : updatedForm.amount) || 0;
            const discount = parseFloat(name === 'total_discount' ? value : updatedForm.total_discount) || 0;
            const net = Math.max(amount - discount, 0);
            updatedForm.net_total = net.toFixed(2);
            updatedForm.grand_total = net.toFixed(2);
        }
        setForm(updatedForm);
    };

    const handleSubmit = async e => {

        e.preventDefault();
        setLoading(true);
        setMessage('');
        const cancelTokenSource = axios.CancelToken.source();
        try {
            const payload = {
                ...form,
                token_number: tokenNumber,
                visit_date: new Date(), // Corrected to use new Date() for visit_date
                is_checked_in: true,
                invoice_date: new Date(), // Updated to use new Date() for invoice_date
            };
            await submitPatientTokenAPI(centerId, selectedPatient?.id, payload, cancelTokenSource.token);
            toast.success('Token generated successfully');
            handleCloseModal()
            setMessage('success: Token generated');
        } catch (err) {
            toast.success('Something went wrong while generating token');
            setMessage('Failed to generate token.');
        }
        setLoading(false);
    };

    const handleGenerateAndPrint = async (e) => {

        e.preventDefault();
        setLoading(true);
        setMessage('');
        const cancelTokenSource = axios.CancelToken.source();
        try {
            const payload = {
                ...form,
                token_number: tokenNumber,
                visit_date: '2025-08-30',
                is_checked_in: true,
                invoice_date: '2025-08-30',
            };
            await submitPatientTokenAPI(centerId, selectedPatient?.id, payload, cancelTokenSource.token);
            // Generate PDF
            const doc = new jsPDF({ format: [80, 130] }); // Small receipt size
            doc.setFontSize(20);
            doc.text(`Token #`, 20, 20);
            doc.setFontSize(32);
            doc.text(`${tokenNumber.toString().padStart(2, '0')}`, 47, 20);
            doc.setFontSize(10);
            doc.text(`IHC Hospital, Lahore`, 24, 26);
            doc.setFontSize(10);
            let y = 35;
            const addRow = (label, value) => {
                doc.text(`${label}:`, 2, y, { align: 'left' });
                doc.text(String(value), 77, y, { align: 'right' });
                y += 8;
            };
            addRow('Patient', selectedPatient?.name || '');
            const doctorName = doctors.find(d => d.id === form.doctor_id)?.name || '';
            addRow('Doctor', doctorName);
            addRow('Token type', form.token_type);
            addRow('Payment mode', form.payment_mode);
            addRow('Status', form.payment_status);
            // addRow('Total amount', form.amount);
            // addRow('Discount', form.total_discount);
            // addRow('-----------------------------------------------------------------------------', '-----------------------------------------------------------------------------');

            // addRow('Net total', form.net_total);
            // addRow('Grand total', form.grand_total);
            // Open print dialog
            window.open(doc.output('bloburl'), '_blank').print();
            setMessage('success: Token generated');
            handleCloseModal()
        } catch (err) {
            setMessage('Failed to generate token or print invoice.');
        }
        setLoading(false);
    };

    return (
        <Card className="main-card mb-3">
            <CardBody>
                {message && <Alert color={message.includes('success') ? 'success' : 'danger'}>{message}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <div className="d-flex justify-content-center mb-3">
                        <h3>Token # <span style={{ fontSize: '60px' }}>{tokenNumber != '' ? tokenNumber : (
                            <Spinner size="sm" color="primary" style={{ marginLeft: 8 }} />
                        )}</span></h3>
                    </div>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Patient</Label>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Autosuggest
                                        suggestions={patientSuggestions}
                                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                                        onSuggestionsClearRequested={onSuggestionsClearRequested}
                                        getSuggestionValue={getSuggestionValue}
                                        renderSuggestion={renderSuggestion}
                                        inputProps={{
                                            placeholder: 'Search By Name, MRN or Mobile Number',
                                            value: patientSearch,
                                            onChange: (e, { newValue }) => setPatientSearch(newValue),
                                            className: 'form-control width-100',

                                        }}
                                        onSuggestionSelected={onPatientSelected}
                                        style={{ width: '100%' }}
                                    />
                                    {searchLoading && <Spinner size="sm" color="primary" style={{ marginLeft: 8 }} />}
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Doctor</Label>
                                <Input type="select" name="doctor_id" value={form.doctor_id} onChange={handleChange} required>
                                    <option value="">Select Doctor</option>
                                    {doctors.map(doc => (
                                        <option key={doc.id} value={doc.id}>{doc.name}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Token Type</Label>
                                <Input type="select" name="token_type" value={form.token_type} onChange={handleChange} required>
                                    {TOKEN_TYPES.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Comments</Label>
                                <Input type="textarea" name="comments" value={form.comments} onChange={handleChange} required />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Payment Mode</Label>
                                <Input type="select" name="payment_mode" value={form.payment_mode} onChange={handleChange} required>
                                    {PAYMENT_MODES.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Payment Status</Label>
                                <Input type="select" name="payment_status" value={form.payment_status} onChange={handleChange} required>
                                    {PAYMENT_STATUSES.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={9} sm={9} xs={9}>
                            <FormGroup>
                                <Label>Total Amount</Label></FormGroup>
                        </Col>
                        <Col md={3} sm={3} xs={3}>
                            <FormGroup>
                                <Input type="number" name="amount" value={form.amount} onChange={handleChange} required placeholder='Total Amount' />
                            </FormGroup>
                        </Col>
                        <Col md={9} sm={9} xs={9}>
                            <FormGroup>
                                <Label>Total Discount</Label>
                            </FormGroup>
                        </Col>
                        <Col md={3} sm={3} xs={3}>
                            <FormGroup>
                                <Input type="number" placeholder='Discount' name="total_discount" value={form.total_discount} onChange={handleChange} required />
                            </FormGroup>
                        </Col>
                        <Col md={9} sm={9} xs={9}>
                            <FormGroup>
                                <Label>Net Total</Label>
                            </FormGroup>
                        </Col>
                        <Col md={3} sm={3} xs={3}>
                            <FormGroup>
                                <Input type="number" readOnly placeholder='Net Total' name="net_total" value={form.net_total} onChange={handleChange} required />
                            </FormGroup>
                        </Col>
                        <Col md={9} sm={9} xs={9}>
                            <FormGroup>
                                <Label>Grand Total</Label>
                            </FormGroup>
                        </Col>
                        <Col md={3} sm={3} xs={3}>
                            <FormGroup>
                                <Input type="number" readOnly placeholder='Grand Total' name="grand_total" value={form.grand_total} onChange={handleChange} required />
                            </FormGroup>
                        </Col>

                    </Row>
                    <div className="d-flex justify-content-between  mt-3">
                        <Button color="primary" type="submit" disabled={loading || !selectedPatient || !form.doctor_id || !form.amount} style={{ marginRight: 8 }}>{loading ? 'Submitting...' : 'Generate Token'}</Button>
                        <Button color="success" onClick={handleGenerateAndPrint} disabled={loading || !selectedPatient || !form.doctor_id || !form.amount}>Generate & Print Invoice</Button>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
};

export default TokenManagement;